import { useTranslation } from '@thoughtspot/i18n';
import { Button } from '@thoughtspot/radiant-react/widgets/button';
import { Horizontal } from '@thoughtspot/radiant-react/widgets/layout';
import { Link } from '@thoughtspot/radiant-react/widgets/link';
import { TableListView } from '@thoughtspot/radiant-react/widgets/table/table-list-view';
import { Colors, Typography, } from '@thoughtspot/radiant-react/widgets/typography';
import { isAdminModernExperienceEnabled } from '@thoughtspot/session-service';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useDeleteHelpConFig, useEditHelpConfig, useGetHelpConfigListQuery, useGetReleaseVersionQuery, useResetHelpConfig, useSetHelpConfig, } from '../../../services/system/system-service';
import { LoaderIndicator } from '../../loader';
import { Content, CreateModal, Header, Seperator, WithQueryHandler, } from '../../system/common';
import { ActionButtons, LearnMoreConfig, Mode, } from '../../system/common/constants';
import { getConfig } from '../../system/common/form-builder-config';
import { getFormFields } from '../../system/common/utils';
import { contentConfig, convertBlobToBase64, flattenObject, getCustomIcon, headerConfig, ImagePreview, tableListViewColumns, } from './config.container';
import { MenuItemStatusMapping, TranslationKeys, } from './constants';
import styles from './help-customization.module.scss';
export const HelpCustomisationComponent = ({ onSuccess, onError, showLoader, hideLoader, }) => {
    const { t } = useTranslation();
    const [mode, setMode] = useState(Mode.VIEW);
    const [formType, setFormType] = useState('View');
    const [modal, setIsModal] = useState({ isOpen: false, action: 'reset' });
    const [formFields, setFormFields] = useState([]);
    const [newHelpConfig, updateHelpConfig] = useState();
    const [deleteConfigId, setDeleteConfigId] = useState();
    const [currentConfigId, setConfigId] = useState();
    const initialConfig = useMemo(() => getConfig('helpCustomization'), []);
    const [imgSrc, setImgSrc] = useState('');
    const [hasImgUpload, setHasImgUpload] = useState(false);
    const [menuItems, setMenuItems] = useState([]);
    const { data: helpConfigList, loading: isHelpConfigLoading, error, } = useGetHelpConfigListQuery();
    const { data: releaseVersion, loading: isReleaseVersionLoading, } = useGetReleaseVersionQuery();
    const getTranslations = (key, args) => t(`${key}`, args);
    const checkLoadingState = isHelpConfigLoading || isReleaseVersionLoading;
    const getFieldConfig = (field) => field.config;
    const getAdditionlFieldAttribute = (field) => {
        if (field.key === 'itemStatus') {
            return {
                true: t(TranslationKeys.HELP_CUSTOMIZATION_STATUS_ENABLE),
                false: t(TranslationKeys.HELP_CUSTOMIZATION_STATUS_DISABLE),
            };
        }
        return null;
    };
    const updateFormFields = (formData = {}, formMode, errorMessages = {}) => {
        const fieldsToShow = [];
        initialConfig.forEach((config) => {
            if (config.add && formMode === 'Add') {
                fieldsToShow.push({
                    ...config,
                    configCallback: getFieldConfig,
                });
            }
            else if (formMode === 'Edit') {
                config.valueLabels = getAdditionlFieldAttribute(config);
                fieldsToShow.push({
                    ...config,
                    configCallback: getFieldConfig,
                });
            }
        });
        const newFormFields = getFormFields(formData, fieldsToShow, errorMessages, mode);
        setFormFields([...newFormFields]);
    };
    const goToEdit = (rowData) => {
        setHasImgUpload(true);
        setFormType('Edit');
        setMode(Mode.EDIT);
        setImgSrc(rowData.customIcon);
        hideLoader();
        updateFormFields({ ...rowData }, 'Edit');
        setConfigId(rowData.id);
    };
    const helpConfigData = (helpconfigRawData = []) => {
        const rowData = [];
        helpconfigRawData.map(({ id, title, iconFilePath, url, enabled }) => {
            const newUrl = url.replace('{versionNameForLink}', releaseVersion?.Data?.split('-')[0]);
            return rowData.push({
                id,
                menuItemName: { text: title },
                menuItemIcon: { iconPath: getCustomIcon(iconFilePath, id) },
                menuItemURL: {
                    href: newUrl,
                    text: newUrl,
                    target: '_blank',
                },
                menuItemStatus: {
                    text: MenuItemStatusMapping()[0][enabled.toString()],
                },
                menuItemEditAction: {
                    text: t(TranslationKeys.HELP_CUSTOMIZATION_EDIT_BUTTON),
                    onClick: () => {
                        showLoader();
                        const currRow = {
                            id,
                            itemStatus: enabled,
                            mount_item_label: title,
                            item_url: url,
                            mount_item_icon: `${title}.png`,
                            customIcon: getCustomIcon(iconFilePath, id),
                        };
                        goToEdit(currRow);
                    },
                },
            });
        });
        return rowData;
    };
    const deleteIcon = (currentFormRef) => {
        setImgSrc('');
        const formData = currentFormRef.current.onSubmitForm();
        const { enabled, title, url } = flattenObject(formData, currentConfigId);
        updateFormFields({
            itemStatus: enabled,
            mount_item_label: title,
            item_url: url,
            mount_item_icon: '',
            iconBase64String: '',
            file: '',
        }, formType);
        setHasImgUpload(false);
    };
    const onMutationComplete = (newUpdatedHelpConfig, messageStr) => {
        hideLoader();
        onSuccess(messageStr, true);
        setFormType('View');
        setMenuItems(helpConfigData(newUpdatedHelpConfig.Data));
        setMode(Mode.VIEW);
        setImgSrc('');
        setHasImgUpload(false);
    };
    const onMutationError = () => {
        onError(getTranslations(TranslationKeys.HELP_CUSTOMIZATION_ERROR_MESSAGE));
    };
    const [resetHelpConfig] = useResetHelpConfig(resetHelpConfigs => onMutationComplete(resetHelpConfigs, getTranslations(TranslationKeys.HELP_CUSTOMIZATION_RESET_SUCCESS)), onMutationError);
    const [deleteHelpConfig] = useDeleteHelpConFig(updatedHelpConfigs => onMutationComplete(updatedHelpConfigs, getTranslations(TranslationKeys.HELP_CUSTOMIZATION_DELETE_SUCCESS)), onMutationError, deleteConfigId);
    const [editmenuItem] = useEditHelpConfig(newUpdatedHelpConfig => onMutationComplete(newUpdatedHelpConfig, getTranslations(TranslationKeys.HELP_CUSTOMIZATION_ADD_EDIT_SUCCESS)), onMutationError, newHelpConfig);
    const [addmenuItem] = useSetHelpConfig(newUpdatedHelpConfig => onMutationComplete(newUpdatedHelpConfig, getTranslations(TranslationKeys.HELP_CUSTOMIZATION_ADD_EDIT_SUCCESS)), onMutationError, newHelpConfig);
    const addEditContentConfig = {
        title: formType === 'Add'
            ? getTranslations(TranslationKeys.HELP_CUSTOMIZATION_ADDFORM_TITLE)
            : getTranslations(TranslationKeys.HELP_CUSTOMIZATION_EDITFORM_TITLE),
        formFields: [...formFields],
    };
    const actions = {
        primary: {
            type: ActionButtons.SAVE,
            text: getTranslations(TranslationKeys.HELP_CUSTOMIZATION_SAVE_BUTTON),
            returnData: true,
        },
        secondary: {
            type: ActionButtons.CANCEL,
            text: getTranslations(TranslationKeys.HELP_CUSTOMIZATION_CANCEL_BUTTON),
        },
        ...(formType === 'Edit' && {
            link: {
                type: ActionButtons.DELETE,
                askConfirm: true,
                text: getTranslations(TranslationKeys.HELP_CUSTOMIZATION_DELETEITEM_BUTTON_CAPTION),
            },
        }),
    };
    const gotoAdd = () => {
        setMode(Mode.EDIT);
        setFormType('Add');
        updateHelpConfig({});
        updateFormFields({
            mount_item_icon: 'default-icon.png',
        }, 'Add');
        setHasImgUpload(true);
        setImgSrc('note');
    };
    useEffect(() => {
        if (helpConfigList && releaseVersion) {
            const data = helpConfigData(helpConfigList.Data);
            setMenuItems(data);
        }
    }, [helpConfigList, releaseVersion]);
    useEffect(() => {
        if (newHelpConfig && !_.isEmpty(newHelpConfig) && formType === 'Add') {
            addmenuItem();
        }
        if (newHelpConfig && !_.isEmpty(newHelpConfig) && formType === 'Edit') {
            editmenuItem();
        }
    }, [newHelpConfig]);
    useEffect(() => {
        if (deleteConfigId) {
            deleteHelpConfig();
        }
    }, [deleteConfigId]);
    const validateForm = (currentFormData = []) => {
        let isformvalid = true;
        for (const element of currentFormData) {
            if (element.valid === false) {
                isformvalid = false;
                return isformvalid;
            }
        }
        return isformvalid;
    };
    const actionHandler = (type, currentFormData = []) => {
        switch (type) {
            case ActionButtons.ADD:
                gotoAdd();
                break;
            case ActionButtons.SAVE:
                if (validateForm(currentFormData)) {
                    showLoader();
                    const obj = flattenObject(currentFormData, formType === 'Edit' ? currentConfigId : '');
                    if (formType === 'Edit' && !hasImgUpload) {
                        obj.removeIcon = true;
                        delete obj.fileName;
                        delete obj.iconBase64String;
                    }
                    updateHelpConfig(obj);
                }
                break;
            case ActionButtons.CANCEL:
                setMode(Mode.VIEW);
                setFormType('View');
                setHasImgUpload(false);
                break;
            case ActionButtons.DELETE: {
                setMode(Mode.VIEW);
                setFormType('View');
                setDeleteConfigId(currentConfigId);
                setMenuItems([]);
                showLoader();
                break;
            }
            default:
                setMode(Mode.VIEW);
        }
    };
    const onEdit = (currentFormData, id) => {
        if (id === 'mount_item_icon') {
            const currObj = currentFormData.find((currConfig) => currConfig?.file && currConfig.id === 'mount_item_icon');
            if (currObj?.file.name.slice(currObj?.file.name.lastIndexOf('.')) === '.png' &&
                currObj?.file.size < 100000) {
                setHasImgUpload(true);
                convertBlobToBase64(currObj?.file)
                    .then((resp) => {
                    setImgSrc(resp[1]);
                    currObj.iconBase64String = resp[0];
                    currObj.value = currObj.file.name;
                    const { title, url, enabled } = flattenObject(currentFormData);
                    updateFormFields({
                        ...(formType === 'Edit' && {
                            itemStatus: enabled,
                        }),
                        mount_item_label: title,
                        item_url: url,
                        mount_item_icon: currObj.value,
                        iconBase64String: currObj.iconBase64String,
                        file: currObj?.file,
                    }, formType);
                    return '';
                })
                    .catch(() => _.noop());
            }
            else {
                setHasImgUpload(false);
            }
        }
    };
    const onModalConfirm = () => {
        if (formType === 'View') {
            resetHelpConfig();
        }
        else {
            setMode(Mode.VIEW);
            setFormType('View');
            setHasImgUpload(false);
            setImgSrc('');
        }
        setIsModal({ isOpen: false, action: 'reset' });
    };
    const renderImagePreview = () => {
        if (hasImgUpload) {
            return React.createElement(ImagePreview, { onAction: deleteIcon, src: imgSrc });
        }
        return null;
    };
    return checkLoadingState ? (React.createElement(LoaderIndicator, { hideLoader: false })) : (React.createElement(React.Fragment, null,
        mode === Mode.VIEW && !isAdminModernExperienceEnabled() && (React.createElement("div", null,
            React.createElement("div", { className: styles.helpCustomisationHeaderContainer },
                React.createElement(Header, { ...headerConfig()[0], titleSize: "large", mode: Mode.VIEW })),
            React.createElement(Seperator, null),
            React.createElement(Content, { config: contentConfig()[0], actions: actions, actionHandler: actionHandler, mode: Mode.VIEW }))),
        mode === Mode.VIEW && isAdminModernExperienceEnabled() && (React.createElement(Horizontal, { vAlignContent: "center", className: styles.helpDescriptionRowModern },
            React.createElement(Typography, { variant: "body-normal", color: Colors.grayLight, noMargin: true },
                t(TranslationKeys.HELP_CUSTOMIZATION_LEARNMORE),
                "\u00A0",
                React.createElement(Link, { href: LearnMoreConfig.helpCustomization.url, target: "_blank", rel: "noreferrer" }, t('LEARN_MORE'))),
            React.createElement(Button, { type: "primary", text: getTranslations(TranslationKeys.HELP_CUSTOMIZATION_ADD_MENUITEM), onClick: () => actionHandler(ActionButtons.ADD) }))),
        mode === Mode.VIEW && menuItems.length > 0 && (React.createElement("div", null,
            React.createElement(TableListView, { data: menuItems, columns: tableListViewColumns(), options: { hasCheckboxes: false }, headerHeight: 45, loading: false }),
            !isAdminModernExperienceEnabled() && React.createElement(Seperator, null),
            React.createElement("div", { className: styles.resetButtonPosition },
                React.createElement(Button, { type: "tertiary", text: getTranslations(TranslationKeys.HELP_CUSTOMIZATION_RESETTODEFAULT_CAPTION), onClick: () => setIsModal({
                        isOpen: true,
                        action: 'reset',
                    }) })))),
        modal.isOpen && (React.createElement(CreateModal, { onConfirm: onModalConfirm, pageName: "help_customization", onDismiss: () => setIsModal({ isOpen: false }), action: modal?.action })),
        (formType === 'Add' || formType === 'Edit') && (React.createElement(Content, { pageName: "help_customization", key: `${formType.toLocaleLowerCase()}_menu_item`, mode: Mode.EDIT, config: addEditContentConfig, actions: actions, actionHandler: actionHandler, onEdit: onEdit }, renderImagePreview()))));
};
export const HelpCustomization = () => (React.createElement(WithQueryHandler, null,
    React.createElement(HelpCustomisationComponent, null)));
//# sourceMappingURL=help-customization.container.js.map